import { Socket } from 'socket.io-client';
import { ChatMessage, ChatSession, SocketChatSession } from '../types/chat';
import { SocketMessage, SocketType } from '../types/socket';
import { UserRole } from '../types/user';
import { dateReviver, formatMessageTime } from '../utils/date';

export const toSocketChatSession = (
  session: SocketChatSession | ChatSession | null | undefined
): SocketChatSession | null => {
  if (!session) {
    return null;
  }
  const { messages: _messages, ...socketSession } = session as ChatSession;
  return socketSession;
};

export class ChatHelper {
  private currentClientRef: React.MutableRefObject<SocketChatSession | null>;
  private messages: ChatMessage[];
  private getServerNow?: () => Date;

  constructor(
    currentClientRef: React.MutableRefObject<SocketChatSession | null>,
    messages: ChatMessage[],
    getServerNow?: () => Date
  ) {
    this.currentClientRef = currentClientRef;
    this.messages = messages;
    this.getServerNow = getServerNow;
  }

  getSessionAssign = () => {
    return this.currentClientRef.current?.assign;
  };

  isSessionClosed = () => {
    return this.currentClientRef.current?.assign?.closed !== undefined && this.currentClientRef.current?.assign?.closed !== null;
  };

  isSessionEnded = () => {
    const session = this.currentClientRef.current;
    const assignEnded = session?.assign?.end !== undefined && session?.assign?.end !== null;
    const botEnded = session?.endedDate !== undefined && session?.endedDate !== null;
    return assignEnded || botEnded;
  };

  isConnectionLost = () => {
    return this.currentClientRef.current?.assign?.endByRole === UserRole.System;
  };

  showTimeBar(message: ChatMessage): boolean {
    // Get the index of the current message
    const currentIndex = this.messages.findIndex((m: ChatMessage) => m.id === message.id);

    // If it's the first message, always show time
    if (currentIndex === 0) {
      return true;
    }

    // Get the previous message
    const previousMessage = this.messages[currentIndex - 1];

    // If there's no previous message, show time
    if (!previousMessage) {
      return true;
    }

    // Check if current message is in the same time slot as previous message
    const now = this.getServerNow?.();
    const currentTimeSlot = formatMessageTime(message.created, now);
    const previousTimeSlot = formatMessageTime(previousMessage.created, now);

    // If time slots are different, show time
    if (currentTimeSlot !== previousTimeSlot) {
      return true;
    }

    // If we get here, it means the message is in the same time slot as the previous message
    // and it's not the first message, so don't show time
    return false;
  }

  isFirstMessage(): boolean {
    return this.messages.length === 0;
  };

  sendReadStatus = (socket: React.MutableRefObject<Socket | null>, messages: ChatMessage[]) => {
    if (!socket.current?.connected || messages.length === 0) {
      return;
    }

    const readMessage: SocketMessage = {
      session: this.currentClientRef.current || null,
      readMessages: messages,
      socket: SocketType.READ,
      senderId: this.currentClientRef.current?.user?.id,
      timestamp: this.getServerNow?.() ?? new Date()
    };
    socket.current.emit('message', readMessage);
  };

  private showSplitBarAfterMessage = (
    messageId: string,
    splitSource: Date | string | null | undefined
  ): boolean => {
    if (!splitSource) {
      return false;
    }

    const splitTime = dateReviver(splitSource);
    const normalizedMessages = this.messages.map(message => ({
      ...message,
      created: dateReviver(message.created)
    }));

    const lastMessageBeforeSplit = normalizedMessages.filter(message => message.created < splitTime);
    if (
      lastMessageBeforeSplit.length > 0
      && lastMessageBeforeSplit[lastMessageBeforeSplit.length - 1].id === messageId
    ) {
      return true;
    }
    return false;
  };

  public showLiveChatBar = (messageId: string): boolean => {
    const session = this.currentClientRef.current;
    return this.showSplitBarAfterMessage(messageId, session?.assign?.start);
  };

  public showWaitingForAgentBar = (messageId: string): boolean => {
    const session = this.currentClientRef.current;
    if (session?.assign?.start || !session?.liveChatRedirect) {
      return false;
    }

    if (this.messages.length === 0) {
      return false;
    }

    // liveChatRedirect is set before the bot's redirect reply arrives, so anchor
    // the waiting bar after the latest message (the redirect acknowledgment).
    return this.messages[this.messages.length - 1].id === messageId;
  };

  // Show live chat / bot-phase end bar after the last message before the end timestamp.
  // Bot-phase ChatEnded includes a closing message with endedDate and no assign — show after that last message.
  public showLiveChatEndBar = (message: ChatMessage): boolean => {
    const session = this.currentClientRef.current;
    const assignEnd = session?.assign?.end;
    const botEndedDate = session?.endedDate;
    const endTime = assignEnd ?? botEndedDate;
    if (!endTime) {
      return false;
    }

    if (this.messages.length === 0) {
      return false;
    }

    if (message.id !== this.messages[this.messages.length - 1].id) {
      return false;
    }

    // Live-chat end: bar after the last message strictly before assign.end
    if (assignEnd) {
      return dateReviver(message.created) < dateReviver(assignEnd);
    }

    // Bot-phase end (no assign): always after the closing transcript message
    return true;
  };

  public lastFlowMessage = (message: ChatMessage): string => {
    let messageIndex = this.messages.findIndex((m) => m.id === message.id);
    let nextMessage = this.messages[messageIndex + 1];
    let previousMessage = this.messages[messageIndex - 1];
    if ((!nextMessage || nextMessage.sender?.id !== message.sender?.id) && (!!previousMessage && previousMessage.sender?.id === message.sender?.id)) {
      return 'chatyme-flow-message-last';
    }
    return '';
  };

  public hasFlowMessage = (message: ChatMessage): string => {
    let messageIndex = this.messages.findIndex((m) => m.id === message.id);
    let previousMessage = this.messages[messageIndex - 1];
    if (previousMessage && previousMessage.sender?.id === message.sender?.id) {
      return 'chatyme-flow-message';
    }
    return '';
  };

  public firstFlowMessage = (message: ChatMessage): string => {
    let messageIndex = this.messages.findIndex((m) => m.id === message.id);
    let previousMessage = this.messages[messageIndex - 1];
    let nextMessage = this.messages[messageIndex + 1];
    if ((!previousMessage || previousMessage.sender?.id !== message.sender?.id) && (!!nextMessage && nextMessage.sender?.id === message.sender?.id)) {
      return 'chatyme-flow-message-first';
    }
    return '';
  };
}